import React from 'react';
import { Recipe } from '../../api/Recipe';
import {
  Badge,
  Box,
  CardTitle,
  Flex,
  Heading,
  Text,
  type FlexAlign,
  type FlexJustify,
  type FlexWrap,
  type LayoutClassNameProps,
  type TextTone,
} from '../ui';

interface RecipeTitleBlockProps {
  recipe: Pick<Recipe, 'title' | 'authors' | 'tags'>;
  titleId?: string;
  cardTitle?: boolean;
  titleWrapperAlign?: FlexAlign;
  titleClassName?: string;
  titleLayout?: LayoutClassNameProps;
  titleWrapperFlex?: boolean;
  titleWrapperClassName?: string;
  titleWrapperLayout?: LayoutClassNameProps;
  titleWrapperJustify?: FlexJustify;
  titleWrapperWrap?: FlexWrap;
  titleStyle?: React.CSSProperties;
  authorClassName?: string;
  authorLayout?: LayoutClassNameProps;
  authorTone?: TextTone;
  tagsContainerClassName?: string;
  tagsContainerJustify?: FlexJustify;
  tagsContainerLayout?: LayoutClassNameProps;
  tagClassName?: string;
  tagLayout?: LayoutClassNameProps;
  renderAuthor?: (recipe: Pick<Recipe, 'authors'>) => React.ReactNode;
}

const DEFAULT_AUTHOR_LAYOUT: LayoutClassNameProps = { marginBottom: 2 };
const DEFAULT_TAGS_CONTAINER_LAYOUT: LayoutClassNameProps = { marginTop: 2, marginBottom: 3 };
const DEFAULT_TAG_LAYOUT: LayoutClassNameProps = { marginEnd: 2, marginBottom: 2 };

const RecipeTitleBlock: React.FC<RecipeTitleBlockProps> = ({
  recipe,
  titleId,
  cardTitle = false,
  titleWrapperAlign,
  titleClassName = '',
  titleLayout,
  titleWrapperFlex = false,
  titleWrapperClassName = '',
  titleWrapperLayout,
  titleWrapperJustify,
  titleWrapperWrap,
  titleStyle,
  authorClassName = '',
  authorLayout = DEFAULT_AUTHOR_LAYOUT,
  authorTone = 'muted',
  tagsContainerClassName = '',
  tagsContainerJustify,
  tagsContainerLayout = DEFAULT_TAGS_CONTAINER_LAYOUT,
  tagClassName = '',
  tagLayout = DEFAULT_TAG_LAYOUT,
  renderAuthor,
}) => {
  const defaultAuthor = recipe.authors ? (
    <Text tone={authorTone} className={authorClassName} {...authorLayout}>
      Par {recipe.authors.first_name} {recipe.authors.name}
    </Text>
  ) : null;

  const titleContent = (
    <>
      {cardTitle ? (
        <CardTitle as="h1" id={titleId} className={titleClassName} style={titleStyle} {...titleLayout}>
          {recipe.title}
        </CardTitle>
      ) : (
        <Heading as="h1" id={titleId} className={titleClassName} style={titleStyle} {...titleLayout}>
          {recipe.title}
        </Heading>
      )}
      {renderAuthor ? renderAuthor(recipe) : defaultAuthor}
    </>
  );

  return (
    <>
      {titleWrapperFlex ? (
        <Flex
          align={titleWrapperAlign}
          justify={titleWrapperJustify}
          wrap={titleWrapperWrap}
          className={titleWrapperClassName}
          {...titleWrapperLayout}
        >
          {titleContent}
        </Flex>
      ) : (
        <Box className={titleWrapperClassName} {...titleWrapperLayout}>{titleContent}</Box>
      )}
      {recipe.tags && recipe.tags.length > 0 && (
        <Flex wrap justify={tagsContainerJustify} className={tagsContainerClassName} {...tagsContainerLayout}>
          {recipe.tags.map((tag) => (
            <Badge key={tag.id} variant="secondary" className={tagClassName} {...tagLayout}>
              {tag.name}
            </Badge>
          ))}
        </Flex>
      )}
    </>
  );
};

export default RecipeTitleBlock;
